import { IkeId } from '@ikegps/core-shared';
import { action, computed, observable, makeObservable } from 'mobx';
import type { T } from '../../i18n';
import { MobxState } from '../../state/mobx';
import { type IkeInputState, InputState, InputStateTypes } from './input.state';

const INPUT_GROUP_TYPE = 'inputstategroup';

export class IkeInputStateGroup extends MobxState implements IkeInputState {
	_type = INPUT_GROUP_TYPE;

	id = IkeId.generate();

	states = observable.map<string, IkeInputState>();

	isLabelShown = true;
	isAutoFocused = false;

	constructor() {
		super();

		makeObservable(this);
	}

	static isInputStateGroup(input: IkeInputState): input is IkeInputStateGroup {
		return input._type === INPUT_GROUP_TYPE;
	}

	@action child<TK>(
		label: T | string,
		id: string,
		type: InputStateTypes = InputStateTypes.TEXT,
		initialValue?: TK
	): InputState<TK> {
		const state = new InputState<TK>(label, id, type);
		this.add(state);

		if (initialValue != null) {
			state.setValue(initialValue);
		}

		return state;
	}

	@action add(input: IkeInputState) {
		if (this.states.has(input.id)) {
			throw new Error('Duplicate state added: ' + input.id);
		}

		this.states.set(input.id, input);

		if (!this.isLabelShown) {
			input.isLabelShown = this.isLabelShown;
		}
	}

	get<K = string>(id: string): InputState<K> {
		return this.states.get(id) as InputState<K>;
	}

	@action remove(input: IkeInputState) {
		this.states.delete(input.id);
	}

	@action reset() {
		this.states.forEach((state) => state.reset());
	}

	@computed get isValid(): boolean {
		let isValid = true;
		this.states.forEach((state) => {
			if (!state.isValid) {
				isValid = false;
			}
		});
		return isValid;
	}

	@computed get canSubmit(): boolean {
		if (!this.isValid) {
			return false;
		}
		let isValid = true;
		this.states.forEach((state) => {
			if (IkeInputStateGroup.isInputStateGroup(state)) {
				if (!state.canSubmit) {
					isValid = false;
				}
			} else {
				if (state.isRequired && !state.isFilled) {
					isValid = false;
				}
			}
		});
		return isValid;
	}

	@computed get isFilled(): boolean {
		let isFilled = true;
		this.states.forEach((state) => {
			if (!state.isFilled) {
				isFilled = false;
			}
		});
		return isFilled;
	}

	@computed get isRequired(): boolean {
		let required = false;
		this.states.forEach((state) => {
			if (state.isRequired === true) {
				required = true;
			}
		});
		return required;
	}

	@action setDisabled(disabled: boolean) {
		this.states.forEach((state) => state.setDisabled(disabled));
	}

	@action clearStates() {
		this.states.forEach((state) => state.destroy());
		this.states.clear();
	}

	override destroy() {
		super.destroy();
		this.clearStates();
	}

	setAutoFocus(isAutoFocused: boolean): void {
		// Not applicable
	}
}
